import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
} from '@mlm/ui';
import { activateLeadWithCode, getActivationTerms, listMyCodes } from '../lib/api';
import { prettyRedeemError } from '../lib/redeem-errors';

export type ActivateLeadTarget = { id: string; name: string; hasActiveSlot?: boolean };

/**
 * F4 — create a slot for a downline member using a Code+PIN from the caller's
 * own vault. The leader picks an available activation code (PIN auto-fills,
 * gift-card UX); the new slot is owned by the member and sponsored by the
 * leader. Works for a warm lead (their first slot) AND an already-active member
 * (an extra slot) — the slot limit is enforced server-side.
 */
export function ActivateLeadDialog({
  lead,
  onClose,
}: {
  lead: ActivateLeadTarget | null;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const open = lead !== null;
  const [selectedId, setSelectedId] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [termsAccepted, setTermsAccepted] = useState(false);

  const codesQ = useQuery({
    queryKey: ['my-codes', 'available'],
    queryFn: () => listMyCodes({ onlyAvailable: true }),
    enabled: open,
  });

  // Activation terms the leader accepts on the lead's behalf — only required
  // when the tenant has `require_activation_terms` on.
  const termsQ = useQuery({ queryKey: ['activation-terms'], queryFn: getActivationTerms });
  const needsTerms = !!termsQ.data?.required;

  // Activation-capable codes only (an upgrade-only code can't mint a new slot).
  const codes = useMemo(
    () => (codesQ.data ?? []).filter((c) => c.usageMode !== 'upgrade'),
    [codesQ.data],
  );
  const selected = codes.find((c) => c.id === selectedId) ?? null;

  useEffect(() => {
    if (open) {
      setSelectedId('');
      setError(null);
      setTermsAccepted(false);
    }
  }, [open]);

  const mut = useMutation({
    meta: { successMessage: 'Slot activated.' },
    mutationFn: () => {
      if (!lead || !selected) throw new Error('no_code_selected');
      return activateLeadWithCode({
        userId: lead.id,
        code: selected.code,
        pin: selected.pin,
        termsAccepted: needsTerms ? termsAccepted : undefined,
        termsVersion: needsTerms ? termsQ.data?.version : undefined,
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['my-leads'] });
      qc.invalidateQueries({ queryKey: ['my-codes'] });
      qc.invalidateQueries({ queryKey: ['vault'] });
      qc.invalidateQueries({ queryKey: ['my-slots'] });
      onClose();
    },
    onError: (err: unknown) => {
      const data = (err as { response?: { data?: { error?: string } } }).response?.data;
      setError(prettyRedeemError(data?.error ?? 'redeem_failed'));
    },
  });

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {lead?.hasActiveSlot ? 'Add a slot for' : 'Activate'} {lead?.name ?? 'member'} with a code
          </DialogTitle>
          <DialogDescription>
            Use a Code+PIN from your Vault to create a slot for this member. The slot will be owned
            by them and sponsored by you.
            {lead?.hasActiveSlot ? ' They already have a slot — this adds another.' : ''}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {codesQ.isLoading ? (
            <p className="text-sm text-muted-foreground">Loading your codes…</p>
          ) : codes.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              You have no available activation codes in your Vault. Buy a kit to get one.
            </p>
          ) : (
            <div>
              <label className="text-xs text-muted-foreground">Code from your vault</label>
              <select
                className="w-full h-9 rounded-md border bg-background px-2 text-sm"
                value={selectedId}
                onChange={(e) => setSelectedId(e.target.value)}
              >
                <option value="">Select a code…</option>
                {codes.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.productName} · {c.code}
                    {c.slotType === 'free' ? ' (free slot)' : ''}
                  </option>
                ))}
              </select>
              {selected && (
                <div className="mt-2 grid grid-cols-2 gap-3 text-xs text-muted-foreground">
                  <div>
                    Code: <span className="font-mono text-foreground">{selected.code}</span>
                  </div>
                  <div>
                    PIN: <span className="font-mono text-foreground">{selected.pin}</span>
                  </div>
                </div>
              )}
              {selected?.slotType === 'free' && (
                <p className="mt-2 text-xs text-amber-600">
                  This is a free slot — it's placed in the tree but earns no commissions until
                  upgraded to paid.
                </p>
              )}
            </div>
          )}

          {selected && needsTerms && (
            <div className="space-y-2">
              {termsQ.data?.text && (
                <div className="max-h-40 overflow-y-auto rounded-md border bg-background p-2 text-xs whitespace-pre-wrap">
                  {termsQ.data.text}
                </div>
              )}
              <label className="flex items-start gap-2 text-xs">
                <input
                  type="checkbox"
                  checked={termsAccepted}
                  onChange={(e) => setTermsAccepted(e.target.checked)}
                  className="mt-0.5"
                />
                <span>
                  I confirm this activation <strong>cannot be changed</strong> once done, and I
                  accept the activation terms above on the lead's behalf.
                </span>
              </label>
            </div>
          )}

          {error && <p className="text-xs text-destructive">{error}</p>}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={mut.isPending}>
            Cancel
          </Button>
          <Button
            onClick={() => mut.mutate()}
            disabled={!selected || mut.isPending || (needsTerms && !termsAccepted)}
          >
            {mut.isPending ? 'Working…' : lead?.hasActiveSlot ? 'Add slot' : 'Activate slot'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
